import styled from "styled-components";
import {
    LeftSection,
    SectionHeader,
    SectionTitle,
    ToggleContainer,
    ToggleLabel,
    Card,
    InputRow,
    InputLabel,
    Input,
    InputWrapper,
} from "../shared/styled";

// ─── Layout ──────────────────────────────────────────────

export const Container = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 30px;
    padding: 20px;
    height: 100%;

    @media (max-width: 1200px) {
        gap: 20px;
        padding: 15px;
    }

    @media (max-width: 992px) {
        grid-template-columns: 1fr;
        gap: 20px;
        padding: 15px;
    }

    @media (max-width: 576px) {
        padding: 10px;
        gap: 15px;
    }
`;

// ─── Header & Controls ──────────────────────────────────

export const Toggle = styled.button.attrs<{
    isActive: boolean;
    mainColor: string;
}>(({ isActive }) => ({
    type: "button",
    role: "switch",
    "aria-checked": isActive,
}))<{ isActive: boolean; mainColor: string }>`
    width: 50px;
    height: 26px;
    border-radius: 13px;
    border: none;
    background-color: ${({ isActive, mainColor }) =>
        isActive ? mainColor : "#ccc"};
    position: relative;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &::after {
        content: "";
        position: absolute;
        top: 3px;
        left: ${({ isActive }) => (isActive ? "27px" : "3px")};
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background-color: white;
        transition: left 0.2s ease;
    }
`;

export const SmallToggle = styled.button.attrs<{
    isActive: boolean;
    mainColor: string;
}>(({ isActive }) => ({
    type: "button",
    role: "switch",
    "aria-checked": isActive,
}))<{ isActive: boolean; mainColor: string }>`
    width: 36px;
    height: 20px;
    border-radius: 10px;
    border: none;
    background-color: ${({ isActive, mainColor }) =>
        isActive ? mainColor : "#ccc"};
    position: relative;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &::after {
        content: "";
        position: absolute;
        top: 2px;
        left: ${({ isActive }) => (isActive ? "18px" : "2px")};
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background-color: white;
        transition: left 0.2s ease;
    }
`;

// ─── Language Tabs ───────────────────────────────────────

export const LanguageTabs = styled.div`
    display: flex;
    gap: 4px;
    margin-bottom: 15px;
    flex-wrap: wrap;
`;

export const LanguageTab = styled.button<{
    isActive: boolean;
    mainColor: string;
}>`
    padding: 5px 12px;
    border: 1px solid
        ${({ isActive, mainColor }) => (isActive ? mainColor : "#ddd")};
    border-radius: 6px;
    background-color: ${({ isActive, mainColor }) =>
        isActive ? mainColor : "transparent"};
    color: ${({ isActive }) => (isActive ? "white" : "#666")};
    font-size: 12px;
    font-weight: ${({ isActive }) => (isActive ? "600" : "400")};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${({ mainColor }) => mainColor};
    }
`;

// ─── Input ──────────────────────────────────────────────

export const ResetButton = styled.button<{ mainColor: string }>`
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ mainColor }) => mainColor};
    transition: background-color 0.2s ease;
    border-radius: 4px;

    &:hover {
        background-color: ${({ mainColor }) => `${mainColor}20`};
    }

    svg {
        width: 18px;
        height: 18px;
    }
`;

// ─── Preview Buttons ────────────────────────────────────

// ─── Preview Mode Selector ──────────────────────────────

export const PreviewModeContainer = styled.div`
    display: flex;
    gap: 8px;
    margin-top: 10px;
`;

export const PreviewModeButton = styled.button<{
    isActive: boolean;
    mainColor: string;
}>`
    flex: 1;
    padding: 10px;
    border: 2px solid
        ${({ isActive, mainColor }) => (isActive ? mainColor : "#e0e0e0")};
    border-radius: 8px;
    background-color: ${({ isActive, mainColor }) =>
        isActive ? `${mainColor}10` : "white"};
    color: ${({ isActive, mainColor }) => (isActive ? mainColor : "#666")};
    font-size: 12px;
    font-weight: ${({ isActive }) => (isActive ? "600" : "400")};
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${({ mainColor }) => mainColor};
    }

    svg {
        width: 24px;
        height: 24px;
        fill: ${({ isActive, mainColor }) => (isActive ? mainColor : "#999")};
    }
`;

// Shared layout primitives (see ../shared/styled).
export {
    LeftSection,
    SectionHeader,
    SectionTitle,
    ToggleContainer,
    ToggleLabel,
    Card,
    InputRow,
    InputLabel,
    Input,
    InputWrapper,
};
